/**
 * Treadmills API — typed wrapper matching the actual backend endpoints
 * discovered from the legacy TreadmillsAPI.js.
 *
 * GET  /treadmills          → all treadmills
 * GET  /treadmills/:id      → single treadmill
 * GET  /user-treadmills/:id → treadmills created by a user
 * POST /treadmills          → create
 * PUT  /treadmills          → update (body includes id)
 * DELETE /treadmills/:id    → delete
 */
import apiClient from './client';

/* ── Treadmill types ── */

export interface TreadmillItem {
  id?: string;
  from?: number;
  to?: number;
  duration?: number;

  /* Text */
  text?: string;        // plain text (backward compat, student app)
  richText?: string;    // Lexical EditorState JSON
  textSize?: number;
  textColor?: string;
  textPosition?: 'top' | 'center' | 'bottom' | 'left' | 'right' | string;
  textBackgroundColor?: string;
  hasBackgroundColor?: boolean;

  /* Background / colors */
  backgroundColor?: string;
  coverBackground?: boolean;

  /* Media */
  videoSrc?: string;
  videoName?: string;
  videoVolume?: number;
  videoPlaybackRate?: number;
  videoReplay?: { shouldReplay: boolean; times: number };
  /** Sequential video clips to play. Empty / missing = play whole file. */
  videoClips?: Array<{ start?: number; end?: number }>;

  audioSrc?: string;
  audioName?: string;
  audioVolume?: number;
  audioPlaybackRate?: number;
  audioReplay?: { shouldReplay: boolean; times: number };
  /** Sequential audio clips to play. Empty / missing = play whole file. */
  audioClips?: Array<{ start?: number; end?: number }>;
  /** URL of TTS-generated audio — set separately so Аудио tab stays clean */
  ttsAudio?: string;

  imageSrc?: string;
  /** Fabric.js JSON canvas state — enables non-destructive re-editing of the image */
  imageCanvasState?: string;
  /** Transcript imported from the selected audio/video material */
  mediaTranscript?: string;


  /* Answer behavior */
  inputType?: 'none' | 'keyboard' | 'speaking' | 'gaps-input' | 'gaps-dnd' | 'ordering' | 'matching' | 'choice' | 'highlight' | 'emphasis' | 'grouping' | 'video-recording' | string;
  correctAnswer?: string;    // keyboard / emphasis (char indices) / highlight (word indices)
  hasCountDown?: boolean;
  /** When true, hides the top running progress bar indicator on the slide */
  hideProgressBar?: boolean;
  autoFit?: boolean;
  /** Extra seconds AFTER media ends for the student to complete the task (task-time phase).
   *  Only used when inputType !== 'none' and inputType !== 'speaking' (speaking uses speakLimit). */
  taskTime?: number;
  /** Extra seconds added automatically to media duration (Auto + N sec) */
  taskTimeOffset?: number;
  /** Multiplier for media duration (Auto x N, e.g. x2, x3) */
  taskTimeMultiplier?: number;

  /* Answer-config fields (set by SlideAnswerEditor) */
  /** Separate task text — used in highlight, emphasis, keyboard answer text.
   *  Decoupled from the slide's display text so teachers can show different
   *  content in the slide vs. the exercise task. */
  taskText?: string;
  /** Gap text with [[word]] or [[var1|var2]] markers (gaps-input / gaps-dnd / grouping serialisation) */
  gapText?: string;
  /** Choice / Select options                         (choice) */
  selectOptions?: Array<{ id: string; text: string; isCorrect?: boolean }>;
  /** Match pairs or ordering tiles                   (matching / ordering) */
  matchOptions?: Array<{
    id: string;
    /** Left-column content */
    leftText?: string;
    leftAudioSrc?: string;
    leftImageSrc?: string;
    /** Right-column content */
    text: string;              // kept as primary for backward compat
    rightAudioSrc?: string;
    rightImageSrc?: string;
  }>;
  /** Media type of the left/right column in a matching exercise */
  matchLeftType?:  'text' | 'audio' | 'image';
  matchRightType?: 'text' | 'audio' | 'image';
  /** Max recording seconds (0 = unlimited)           (video-recording) */
  recordingLimit?: number;


  /* Plug (short gap slide) */
  isPlug?: boolean;

  /* Mine (trap slide, max 2 per treadmill) */
  hasMine?: boolean;
  mineAnswer?: string;
  mineLanguage?: { label: string; value: string };

  /* Second text block (e.g. translation at bottom) */
  subText?: string;
  subTextSize?: number;
  subTextColor?: string;
  subTextPosition?: 'top' | 'center' | 'bottom' | 'left' | 'right';
  subTextBackgroundColor?: string;
  subTextHasBackgroundColor?: boolean;

  /* Extra */
  copyrightDescription?: string;
  removeBlock?: boolean;
  shouldRecord?: boolean;
  /** Recording mode for speaking/audio slides:
   *  - 'sequential': Media -> 3-2-1 Countdown/Pause -> Start Beep -> Recording -> End Beep (Default)
   *  - 'classic': Mic starts immediately at 0s. Slide held open after media for response time.
   *  - 'continuous': Seamless full-session audio stitching into 1 file at end of treadmill.
   *  - 'visual_only': Displays REC indicators & timer, but does not save audio blob.
   */
  recordingMode?: 'sequential' | 'classic' | 'continuous' | 'visual_only';
  /** If true, the pre-recording beep is suppressed (speaking / video-recording slides) */
  disableBeep?: boolean;
  /** If true, microphone interaction runs but audio recording file is not saved/stored */
  disableRecording?: boolean;
  /** If true, starts a 3-2-1 countdown overlay and plays beep cues before recording */
  useCountdown?: boolean;
  scale?: unknown;

  /* Slide repetition */
  slideRepeat?: number;    // how many times to show this slide (default 1)
  repeatInterval?: number; // pause in seconds between repeats (default 1)

  [key: string]: unknown;
}

export interface Treadmill {
  id: string;
  name?: string;
  description?: string;
  avatar?: string;
  sourcesText?: string;
  studentTask?: string;
  creatorId?: string;
  access?: 'private' | 'public';
  accessGroupsIds?: string[];
  items?: TreadmillItem[];
  timestamp?: number;
  updatedTimestamp?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

/* ── Helpers ── */

export function normalizeTreadmillItem(item: any): TreadmillItem {
  if (!item || typeof item !== 'object') return item;

  let inputType: TreadmillItem['inputType'] = item.inputType;

  // Map legacy/synonym input types
  if (
    inputType === 'speaking' ||
    inputType === 'voice' ||
    inputType === 'mic' ||
    inputType === 'microphone' ||
    inputType === 'speech' ||
    item.isSpeaking ||
    item.shouldRecord ||
    item.type === 'speaking' ||
    item.answerType === 'speaking' ||
    item.answerType === 'voice' ||
    item.answerType === 'microphone'
  ) {
    inputType = 'speaking';
  } else if (
    inputType === 'keyboard' ||
    inputType === 'input' ||
    inputType === 'text' ||
    item.type === 'keyboard' ||
    item.answerType === 'keyboard'
  ) {
    inputType = 'keyboard';
  } else if (inputType === 'gaps' || inputType === 'gap' || inputType === 'gaps-input') {
    inputType = 'gaps-input';
  } else if (inputType === 'dnd' || inputType === 'gaps-dnd') {
    inputType = 'gaps-dnd';
  } else if (inputType === 'select' || inputType === 'quiz' || inputType === 'test' || inputType === 'choice') {
    inputType = 'choice';
  } else if (inputType === 'match' || inputType === 'matching') {
    inputType = 'matching';
  } else if (inputType === 'order' || inputType === 'sort' || inputType === 'ordering') {
    inputType = 'ordering';
  } else if (inputType === 'video' || inputType === 'video-record' || inputType === 'video-recording') {
    inputType = 'video-recording';
  } else if (!inputType || inputType === 'none') {
    if (item.hasAnswer && (item.correctAnswer || item.taskText)) {
      inputType = 'keyboard';
    } else {
      inputType = 'none';
    }
  }

  return {
    ...item,
    inputType,
    time: item.time ?? item.duration ?? 4,
    duration: item.duration ?? item.time ?? 4,
  };
}

function normalizeTreadmill(raw: any): Treadmill {
  const obj = raw?.result ?? raw ?? {};
  return {
    ...obj,
    id: obj.id || obj._id || '',
    items: Array.isArray(obj.items) ? obj.items.map(normalizeTreadmillItem) : [],
  };
}

/* ── API ── */

const TreadmillsAPI = {
  /** Get ALL treadmills (any user) — GET /treadmills */
  async getAllTreadmills(): Promise<Treadmill[]> {
    const { data } = await apiClient.get<Treadmill[]>('/treadmills');
    const list = (data ?? []) as Treadmill[];
    return (Array.isArray(list) ? list : []).map(normalizeTreadmill);
  },

  /**
   * Get ALL treadmills WITHOUT items[] — GET /v3/treadmills/list
   * 50-100x smaller payload. Use for card lists and pickers.
   * Falls back to getAllTreadmills() if EpTreadmillService is not deployed yet.
   */
  async getLightList(): Promise<Treadmill[]> {
    try {
      const { data } = await apiClient.get<{ result: Treadmill[] }>('/v3/treadmills/list');
      const list = (data as any)?.result ?? (data as any) ?? [];
      return (Array.isArray(list) ? list : []).map(t => normalizeTreadmill({ ...t, items: undefined }));
    } catch {
      return this.getAllTreadmills();
    }
  },

  /**
   * Get user treadmills WITHOUT items[] — GET /v3/treadmills/list?userId=<id>
   * Falls back to getUserTreadmills() if endpoint is not deployed yet.
   */
  async getLightUserList(userId: string): Promise<Treadmill[]> {
    try {
      const { data } = await apiClient.get<{ result: Treadmill[] }>('/v3/treadmills/list', { params: { userId } });
      const list = (data as any)?.result ?? (data as any) ?? [];
      return (Array.isArray(list) ? list : []).map(t => normalizeTreadmill({ ...t, items: undefined }));
    } catch {
      return this.getUserTreadmills(userId);
    }
  },

  /** Get public treadmills (student browse) — GET /treadmills?access=public */
  async getPublicTreadmills(): Promise<Treadmill[]> {
    // Try public param first; if backend ignores it, fall back to all and filter client-side
    const { data } = await apiClient.get<Treadmill[]>('/treadmills', { params: { access: 'public' } });
    const list = (data ?? []) as Treadmill[];
    const all = (Array.isArray(list) ? list : []).map(normalizeTreadmill);
    // Client-side safety filter
    return all.filter(t => (t.access as string | undefined) !== 'private');

  },

  /** Get treadmills owned by a specific user — GET /user-treadmills/:userId */
  async getUserTreadmills(userId: string): Promise<Treadmill[]> {
    const { data } = await apiClient.get<Treadmill[]>(`/user-treadmills/${userId}`);
    const list = (data ?? []) as Treadmill[];
    return (Array.isArray(list) ? list : []).map(normalizeTreadmill);
  },

  /** Get a single treadmill — GET /treadmills/:id */
  async getTreadmill(rawId: string): Promise<Treadmill> {
    if (!rawId || typeof rawId !== 'string') return normalizeTreadmill({});
    const cleanId = rawId.includes('/')
      ? (rawId.split('/').find(part => part.length >= 8 && !part.startsWith('b_') && !part.startsWith('_')) || rawId.split('/')[1] || rawId)
      : rawId;
    if (cleanId.startsWith('b_')) {
      return normalizeTreadmill({ id: cleanId });
    }
    try {
      const { data } = await apiClient.get<Treadmill>(`/v3/treadmills/fast/${cleanId}`);
      return normalizeTreadmill(data);
    } catch {
      try {
        const { data } = await apiClient.get<Treadmill>(`/treadmills/${cleanId}`);
        return normalizeTreadmill(data);
      } catch {
        return normalizeTreadmill({ id: cleanId });
      }
    }
  },

  /** Create — POST /v3/treadmills (fast direct MongoDB, fallback to /treadmills) */
  async createTreadmill(payload: Partial<Treadmill>): Promise<Treadmill> {
    try {
      const { data } = await apiClient.post<any>('/v3/treadmills', payload);
      const res = (data as any)?.result ?? data;
      return normalizeTreadmill(res);
    } catch {
      const { data } = await apiClient.post<Treadmill>('/treadmills', payload);
      return normalizeTreadmill(data);
    }
  },

  /** Update — PUT /v3/treadmills/fast/:id (fast direct MongoDB, fallback to /treadmills) */
  async updateTreadmill(payload: Partial<Treadmill> & { id: string }): Promise<Treadmill> {
    try {
      const { data } = await apiClient.put<any>(`/v3/treadmills/fast/${payload.id}`, payload);
      const res = (data as any)?.result ?? data;
      return normalizeTreadmill(res);
    } catch {
      const { data } = await apiClient.put<Treadmill>('/treadmills', payload);
      return normalizeTreadmill(data);
    }
  },

  /**
   * Fast direct-MongoDB save — PUT /v3/treadmills/fast/:id
   * Bypasses ParseAPI cloud hooks → no 90s timeouts.
   * Deployed via EpTreadmillService.js patch on the server.
   */
  async fastSaveItems(id: string, items: TreadmillItem[]): Promise<void> {
    await apiClient.put(`/v3/treadmills/fast/${id}`, { items }, { timeout: 60_000 });
  },

  /** Save treadmill student answers — POST /v3/treadmills/:treadmillId/save-answer */
  async saveTreadmillAnswer(treadmillId: string, userId: string, data: any): Promise<void> {
    await apiClient.post(`/v3/treadmills/${treadmillId}/save-answer`, { options: data }, { params: { userId } });
  },

  /** Get treadmill student answers — GET /v3/treadmills/:treadmillId/user-answer */
  async getTreadmillAnswer(treadmillId: string, userId: string): Promise<any | null> {
    const { data } = await apiClient.get(`/v3/treadmills/${treadmillId}/user-answer`, { params: { userId } });
    return data;
  },

  /** Delete treadmill student answers — DELETE /v3/treadmills/:treadmillId/user-answer */
  async deleteTreadmillAnswer(treadmillId: string, userId: string): Promise<void> {
    await apiClient.delete(`/v3/treadmills/${treadmillId}/user-answer`, { params: { userId } });
  },

  /** Delete — moves to trash */
  async deleteTreadmill(id: string): Promise<void> {
    await apiClient.delete(`/treadmills/${id}`, { data: { id } });
  },

  /** Get treadmills in trash */
  async getTrash(): Promise<Treadmill[]> {
    const { data } = await apiClient.get<Treadmill[]>('/v3/treadmills/trash');
    return data ?? [];
  },

  /** Restore a treadmill from trash */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/v3/treadmills/${id}/restore`, {});
  },

  /** Permanently delete a treadmill */
  async permanentDelete(id: string): Promise<void> {
    await apiClient.delete(`/v3/treadmills/${id}/permanent`);
  },

  /** Clone a treadmill — creates a copy with '(копия)' appended to the name */
  async cloneTreadmill(id: string): Promise<Treadmill> {
    const original = await this.getTreadmill(id);
    const { id: _id, createdAt: _ca, updatedAt: _ua, timestamp: _ts, updatedTimestamp: _uts, ...rest } = original as any;
    return this.createTreadmill({ ...rest, name: `${original.name ?? ''} (копия)` });
  },
};

export default TreadmillsAPI;
